import type { TestableComponent } from '@mweb/types';
import { Drawer } from '@mweb/zappos-ui/Drawer';
import { useState } from 'react';

import { TabsContainer } from 'components/productdetail/ReviewSummarization/TabsContainer/TabsContainer';
import { useFetchReviewSummarization } from 'hooks/useFetchReviewSummarization';
import useMartyContext from 'hooks/useMartyContext';
import useWindowSize from 'hooks/useWindowSize';
import { isMobileViewport } from 'utils/viewportUtil';

export interface ReviewSummarizationProps extends React.ComponentPropsWithoutRef<'div'>, TestableComponent {
  productId: string;
  /** Callback to let a parent page know this component will render or not */
  isRendered: (isRendered: boolean) => void;
}

export const ReviewSummarization = (props: ReviewSummarizationProps) => {
  const { testId, productId, isRendered } = props;
  const { testId: getTestId } = useMartyContext();
  const { width: windowWidth = 0 } = useWindowSize(200);
  const isMobile = isMobileViewport(windowWidth);
  const [selectedTab, setSelectedTab] = useState('');

  const { reviewSummarization, isFetched } = useFetchReviewSummarization({ productId: productId }) ?? {};
  const { summary, aspects } = reviewSummarization ?? {};

  if (!summary) {
    // If the call is finished and there is no summary we know this component won't render
    if (isFetched) {
      isRendered(false);
    }
    return null;
  }

  isRendered(true);

  const onTabOpen = (tab: string) => {
    setSelectedTab(tab);
  };

  const closeDrawer = () => {
    setSelectedTab('');
  };

  return (
    <div className="flex max-w-4xl flex-col gap-y-4" data-test-id={getTestId(testId)}>
      <h3 className="text-xl font-medium">Customers Say</h3>
      <p className="text-base">{summary}</p>
      <p className="text-[15px] text-disabled">AI-generated from the text of customer reviews.</p>
      {aspects?.length && (
        <TabsContainer
          testId={`${getTestId(testId)}-container`}
          aspects={aspects}
          onTabOpen={onTabOpen}
          selectedTab={selectedTab}
          isSuppressContent={isMobile}
        />
      )}
      {aspects?.length && isMobile && selectedTab && (
        <Drawer open={selectedTab ? true : false} onOpenChange={closeDrawer} position="bottom" title="">
          <TabsContainer isWrapHeaderEnabled={false} testId={`${getTestId(testId)}-container-drawer`} aspects={aspects} selectedTab={selectedTab} />
        </Drawer>
      )}
    </div>
  );
};
